import type { ReactNode } from "react";
import { motion } from "motion/react";
import { ArrowDownRight, ArrowUpRight, Check, type LucideIcon, Minus } from "lucide-react";
import { cn } from "../lib/cn";
import { documentLocale, microcopy } from "../lib/microcopy";
import { LATIN_RUN, arabicTerms, isPassage, recordedTerms, stripDemoTags, useContentLanguage } from "../lib/contentLanguage";
import { AnimatedNumber } from "./motion";
import { Card } from "./card";

// ---------------------------------------------------------------- StatCard
export interface StatCardProps {
  label: ReactNode;
  value: number;
  format?: (n: number) => string;
  unit?: ReactNode;
  target?: number;
  targetLabel?: ReactNode;
  trend?: number[];
  direction?: "up" | "down" | "flat";
  good?: boolean;
  icon?: LucideIcon;
  className?: string;
  footer?: ReactNode;
  tone?: "gold" | "sea" | "camel" | "neutral";
}

export function StatCard({ label, value, format, unit, target, targetLabel, trend, direction = "flat", good = true, icon: Icon, className, footer, tone = "gold" }: StatCardProps) {
  const DirIcon = direction === "up" ? ArrowUpRight : direction === "down" ? ArrowDownRight : Minus;
  // Percentage deltas are meaningless from a near-zero baseline; show the direction only in that case.
  const first = trend?.[0];
  const last = trend?.[trend.length - 1];
  const delta = trend && trend.length > 1 && first !== undefined && last !== undefined && Math.abs(first) >= 1 ? Math.round(((last - first) / Math.abs(first)) * 100) : null;
  return (
    <Card className={cn("relative flex flex-col overflow-hidden p-5", className)} data-tone={tone}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium leading-snug text-sand-800">{label}</p>
        {Icon ? <Icon className="size-4 shrink-0 text-sand-500" aria-hidden /> : null}
      </div>
      <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <AnimatedNumber value={value} format={format} className="font-heading text-3xl font-semibold tabular leading-none text-aeblack-950" />
        {unit ? <span className="text-base text-muted-foreground">{unit}</span> : null}
        {trend && trend.length > 1 ? (
          <span className={cn("ms-auto inline-flex items-center gap-0.5 text-xs font-medium tabular", good ? "text-success-700" : "text-danger-700")}>
            <DirIcon className="size-3.5" />
            {delta !== null && Math.abs(delta) <= 300 ? `${Math.abs(delta)}%` : null}
          </span>
        ) : null}
      </div>
      {target !== undefined ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {targetLabel ?? "Target"}: <span className="font-medium tabular text-foreground">{format ? format(target) : target}</span>
        </p>
      ) : null}
      {trend && trend.length > 1 ? <Sparkline data={trend} className="mt-4 h-9 w-full" good={good} /> : null}
      {footer ? <div className="mt-4 border-t border-sand-200 pt-3 text-xs leading-relaxed text-muted-foreground">{footer}</div> : null}
    </Card>
  );
}

/** Horizontal stage bars — a legible, responsive alternative to a funnel chart. */
export function StageBars({ stages, className, format }: { stages: Array<{ name: ReactNode; value: number; hint?: ReactNode; color?: string }>; className?: string; format?: (n: number) => string }) {
  const max = Math.max(1, ...stages.map((s) => s.value));
  return (
    <ol className={cn("space-y-3", className)}>
      {stages.map((s, i) => (
        <li key={i} className="grid grid-cols-[minmax(0,11rem)_minmax(0,1fr)_3rem] items-center gap-4 text-sm">
          <span className="truncate text-aeblack-900" title={typeof s.name === "string" ? s.name : undefined}>{s.name}</span>
          <span className="h-2.5 overflow-hidden rounded-full bg-sand-100">
            <motion.span className={cn("block h-full rounded-full", !s.color && "bg-aegold-600")} initial={{ width: 0 }} animate={{ width: `${Math.max(2, (s.value / max) * 100)}%` }} transition={{ duration: 0.8, ease: "easeOut", delay: i * 0.05 }} style={s.color ? { background: s.color } : { opacity: 1 - i * 0.07 }} />
          </span>
          <span className="text-end tabular font-medium text-aeblack-950">{format ? format(s.value) : s.value}</span>
        </li>
      ))}
    </ol>
  );
}

/** Single stacked bar with a legend — for small categorical breakdowns (decision outcomes, statuses). */
/** A proportion bar with its legend; given `onSegmentClick`, each segment and legend row opens what it counts. */
export function SegmentBar({ segments, className, palette, onSegmentClick }: { segments: Array<{ name: ReactNode; value: number }>; className?: string; palette: readonly string[]; onSegmentClick?: (index: number) => void }) {
  const total = segments.reduce((a, b) => a + b.value, 0);
  return (
    <div className={className}>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-sand-100">
        {segments.map((s, i) => (s.value > 0 ? (onSegmentClick
          ? <button key={i} type="button" aria-label={`${typeof s.name === "string" ? s.name : ""} ${s.value}`} onClick={() => onSegmentClick(i)} className="h-full cursor-pointer hover:opacity-80" style={{ width: `${(s.value / Math.max(1, total)) * 100}%`, background: palette[i % palette.length] }} title={`${typeof s.name === "string" ? s.name : ""} ${s.value}`} />
          : <span key={i} className="h-full" style={{ width: `${(s.value / Math.max(1, total)) * 100}%`, background: palette[i % palette.length] }} title={`${typeof s.name === "string" ? s.name : ""} ${s.value}`} />) : null))}
      </div>
      <ul className="mt-3 space-y-1.5 text-xs">
        {segments.map((s, i) => {
          const row = (
            <>
              <span className="size-2.5 shrink-0 rounded-full" style={{ background: palette[i % palette.length] }} />
              <span className="min-w-0 flex-1 truncate text-aeblack-900">{s.name}</span>
              <span className={onSegmentClick ? "tabular text-muted-foreground underline underline-offset-4" : "tabular text-muted-foreground"}>{s.value}</span>
            </>
          );
          return <li key={i}>{onSegmentClick ? <button type="button" onClick={() => onSegmentClick(i)} className="flex w-full items-center gap-2 rounded-sm text-start hover:bg-sand-50">{row}</button> : <div className="flex items-center gap-2">{row}</div>}</li>;
        })}
      </ul>
    </div>
  );
}

export function Sparkline({ data, className, good = true }: { data: number[]; className?: string; good?: boolean }) {
  const w = 100;
  const h = 32;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((d, i) => `${(i / (data.length - 1)) * w},${h - ((d - min) / range) * (h - 4) - 2}`).join(" ");
  const color = good ? "var(--color-success-600)" : "var(--color-danger-600)";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={className} preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, ease: "easeOut" }} />
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill="url(#spark-fill)" />
    </svg>
  );
}

// ---------------------------------------------------------------- SLA ring
/** Lapsed: a period given to a party (or awaiting the Minister) ran to its end without the step it waited for – not a timely completion. */
export type SlaHealth = "OnTrack" | "AtRisk" | "Breached" | "Completed" | "Paused" | "Lapsed";

export interface SlaRingProps {
  percent: number; // 0-100 elapsed
  daysRemaining: number;
  health: SlaHealth;
  label?: ReactNode;
  size?: number;
  className?: string;
}

const SLA_ARC: Record<SlaHealth, string> = {
  OnTrack: "var(--color-success-600)",
  AtRisk: "var(--color-warning-500)",
  Breached: "var(--color-danger-600)",
  Completed: "var(--color-sand-500)",
  Paused: "var(--color-sand-400)",
  Lapsed: "var(--color-warning-700)",
};

/**
 * The days of a statutory period as one phrase, with the Arabic number agreement (1, 2, 3–10, 11–99, 100 and above): "بقي يومان",
 * "بقي 15 يوما", "تأخر 3 أيام"; a paused period says what its number is ("موقوفة · بقي 8 أيام").
 */
export function deadlinePhrase(daysRemaining: number, health: SlaHealth): string {
  const n = Math.abs(daysRemaining);
  const overdue = daysRemaining < 0;
  const ar = documentLocale() === "ar";
  let days: string;
  if (ar) {
    const mod = n % 100;
    const count = n === 0 ? "" : n === 1 ? "يوم واحد" : n === 2 ? "يومان" : mod >= 3 && mod <= 10 ? `${n} أيام` : mod >= 11 && mod <= 99 ? `${n} يوما` : `${n} يوم`;
    const countAcc = n === 1 ? "يوما واحدا" : n === 2 ? "يومين" : count;
    days = n === 0 ? (overdue ? "انتهت المدة اليوم" : "ينتهي اليوم") : overdue ? `تأخر ${countAcc}` : `بقي ${count}`;
  } else {
    days = n === 0 ? "Due today" : overdue ? `${n} ${n === 1 ? "day" : "days"} overdue` : `${n} ${n === 1 ? "day" : "days"} left`;
  }
  return health === "Paused" ? `${microcopy("paused")} · ${days}` : days;
}

/** What each colour of a deadline ring means; shown once beneath a list of rings. */
export function DeadlineKey({ className }: { className?: string }) {
  const ar = documentLocale() === "ar";
  const items: Array<[string, string]> = ar
    ? [[SLA_ARC.OnTrack, "ضمن المدة"], [SLA_ARC.AtRisk, "مضت أربعة أخماس المدة أو بقي يومان أو أقل"], [SLA_ARC.Breached, "تجاوز المدة"], [SLA_ARC.Paused, "موقوفة"]]
    : [[SLA_ARC.OnTrack, "Within the period"], [SLA_ARC.AtRisk, "Four fifths elapsed or two days or fewer left"], [SLA_ARC.Breached, "Past the period"], [SLA_ARC.Paused, "Paused"]];
  return (
    <p className={cn("flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground", className)}>
      {items.map(([color, label]) => (
        <span key={label} className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full" style={{ background: color }} aria-hidden />{label}</span>
      ))}
    </p>
  );
}

/**
 * Deadline indicator: a ring showing the elapsed share of the statutory period, and beside it the days as one phrase. The colour says
 * where the period stands (see DeadlineKey); there is no icon to decode.
 */
export function SlaRing({ percent, daysRemaining, health, label, size = 64, className }: SlaRingProps) {
  const stroke = Math.max(3, Math.round(size / 12));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, percent));
  const overdue = daysRemaining < 0;
  const showDays = health !== "Completed" && health !== "Lapsed";
  const textClass = size >= 64 ? "text-base" : "text-xs";
  const phrase = deadlinePhrase(daysRemaining, health);
  return (
    <div className={cn("inline-flex items-center gap-2", className)} title={phrase}>
      <div className="relative shrink-0" style={{ width: size, height: size }} aria-hidden>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--color-sand-200)" strokeWidth={stroke} fill="none" />
          <motion.circle cx={size / 2} cy={size / 2} r={r} stroke={SLA_ARC[health]} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (p / 100) * c }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} />
        </svg>
      </div>
      {showDays ? <span className={cn("whitespace-nowrap font-medium tabular text-aeblack-900", textClass, overdue && "text-danger-700", health === "Paused" && "text-sand-700")}>{phrase}</span> : null}
      {label ? <div className={cn("min-w-0 text-sm", showDays && "border-s border-sand-200 ps-2.5")}>{label}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Risk gauge
export function RiskGauge({ score, className, size = 180, label }: { score: number; className?: string; size?: number; label?: ReactNode }) {
  const s = Math.max(0, Math.min(100, score));
  const r = size / 2 - 12;
  const cx = size / 2;
  const cy = size / 2;
  const startAngle = -210;
  const sweep = 240;
  const polar = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const arc = (from: number, to: number) => {
    const a = polar(from);
    const b = polar(to);
    const large = to - from > 180 ? 1 : 0;
    return `M ${a.x} ${a.y} A ${r} ${r} 0 ${large} 1 ${b.x} ${b.y}`;
  };
  const needleAngle = startAngle + (s / 100) * sweep;
  const tip = polar(needleAngle);
  const band = microcopy(s >= 70 ? "riskHigh" : s >= 40 ? "riskElevated" : "riskLow");
  const bandColor = s >= 70 ? "text-danger-600" : s >= 40 ? "text-warning-600" : "text-success-600";
  return (
    <div className={cn("flex flex-col items-center", className)}>
      <svg width={size} height={size * 0.78} viewBox={`0 0 ${size} ${size * 0.78}`}>
        <path d={arc(startAngle, startAngle + sweep * 0.4)} stroke="var(--color-success-500)" strokeWidth="12" fill="none" strokeLinecap="round" opacity="0.85" />
        <path d={arc(startAngle + sweep * 0.4, startAngle + sweep * 0.7)} stroke="var(--color-warning-500)" strokeWidth="12" fill="none" opacity="0.85" />
        <path d={arc(startAngle + sweep * 0.7, startAngle + sweep)} stroke="var(--color-danger-500)" strokeWidth="12" fill="none" strokeLinecap="round" opacity="0.85" />
        <motion.line x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke="var(--color-aeblack-950)" strokeWidth="3" strokeLinecap="round" initial={{ rotate: -((s / 100) * sweep), originX: `${cx}px`, originY: `${cy}px` }} animate={{ rotate: 0 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }} />
        <circle cx={cx} cy={cy} r="6" fill="var(--color-aeblack-950)" />
        <text x={cx} y={cy + 34} textAnchor="middle" className="fill-aeblack-950 font-heading" style={{ fontSize: 28, fontWeight: 600 }}>{s}</text>
      </svg>
      <div className={cn("-mt-2 text-sm font-semibold", bandColor)}>{band}</div>
      {label ? <div className="text-xs text-muted-foreground">{label}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Timeline
export interface TimelineItem {
  id: string;
  title: ReactNode;
  detail?: ReactNode;
  time: ReactNode;
  actor?: ReactNode;
  kind?: "human" | "agent" | "system" | "milestone";
  icon?: LucideIcon;
}

export function Timeline({ items, className }: { items: TimelineItem[]; className?: string }) {
  return (
    <ol className={cn("relative ms-3 border-s border-sand-300", className)}>
      {items.map((item, i) => {
        const Icon = item.icon;
        const dot = item.kind === "agent" ? "bg-gradient-to-br from-aegold-400 to-seablue-400" : item.kind === "milestone" ? "bg-aegold-600" : item.kind === "system" ? "bg-sand-400" : "bg-aeblack-800";
        return (
          <motion.li key={item.id} className="relative ps-6 pb-6 last:pb-0" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
            <span data-slot="timeline-dot" data-kind={item.kind ?? "human"} className={cn("absolute -start-[7px] top-1 grid size-3.5 place-items-center rounded-full ring-4 ring-background", dot)}>
              {Icon ? <Icon className="size-2 text-white" /> : null}
            </span>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <p className="text-sm font-medium text-foreground">{item.title}</p>
              <time className="text-xs text-muted-foreground tabular">{item.time}</time>
            </div>
            {item.detail ? <p className="mt-0.5 text-sm text-muted-foreground">{item.detail}</p> : null}
            {item.actor ? <p className="mt-1 text-xs text-sand-600">{item.actor}</p> : null}
          </motion.li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------- Stepper
export interface Step {
  key: string;
  label: ReactNode;
  description?: ReactNode;
}

export function Stepper({ steps, current, className, onStepClick }: { steps: Step[]; current: number; className?: string; onStepClick?: (i: number) => void }) {
  return (
    <ol className={cn("flex w-full items-center gap-2", className)}>
      {steps.map((step, i) => {
        const state = i < current ? "done" : i === current ? "active" : "todo";
        return (
          <li key={step.key} className="flex flex-1 items-center gap-2 min-w-0">
            <button
              type="button"
              disabled={!onStepClick || i > current}
              onClick={() => onStepClick?.(i)}
              className={cn("flex items-center gap-2.5 text-start min-w-0 rounded-lg px-1 py-0.5 transition-colors", onStepClick && i <= current && "hover:bg-sand-100")}
            >
              <motion.span
                layout
                className={cn(
                  "grid size-8 shrink-0 place-items-center rounded-full text-xs font-semibold ring-4 ring-background transition-colors",
                  state === "done" && "bg-aegold-600 text-white",
                  state === "active" && "bg-aeblack-950 text-white animate-pulse-gold",
                  state === "todo" && "bg-sand-200 text-sand-700",
                )}
              >
                {state === "done" ? <Check className="size-4" strokeWidth={3} /> : i + 1}
              </motion.span>
              <span className="hidden min-w-0 sm:block">
                <span className={cn("block truncate text-sm font-medium", state === "todo" ? "text-muted-foreground" : "text-foreground")}>{step.label}</span>
                {step.description ? <span className="block truncate text-xs text-muted-foreground">{step.description}</span> : null}
              </span>
            </button>
            {i < steps.length - 1 ? <span className={cn("h-px flex-1 rounded bg-sand-300 transition-colors", i < current && "bg-aegold-500")} /> : null}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------- Empty state
export function EmptyState({ icon: Icon, title, description, action, className }: { icon?: LucideIcon; title: ReactNode; description?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div data-slot="empty-state" className={cn("flex flex-col items-center justify-center rounded-2xl border border-dashed border-sand-300 bg-sand-50/60 px-6 py-14 text-center", className)}>
      {Icon ? (
        <span data-slot="empty-state-icon" className="mb-4 grid size-14 place-items-center rounded-2xl bg-gradient-to-br from-aegold-100 to-camel-100 text-aegold-800 animate-float">
          <Icon className="size-7" />
        </span>
      ) : null}
      <h3 className="text-base font-semibold">{title}</h3>
      {description ? <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Key/value list
export function KeyValue({ items, className, columns = 2 }: { items: Array<{ label: ReactNode; value: ReactNode }>; className?: string; columns?: 1 | 2 | 3 }) {
  return (
    <dl className={cn("grid gap-x-6 gap-y-3", columns === 1 && "grid-cols-1", columns === 2 && "grid-cols-1 sm:grid-cols-2", columns === 3 && "grid-cols-1 sm:grid-cols-3", className)}>
      {items.map((it, i) => (
        <div key={i} className="min-w-0" data-empty={it.value == null || it.value === "" || it.value === "—" ? "" : undefined}>
          <dt className="text-xs uppercase tracking-wider text-sand-600">{it.label}</dt>
          <dd className="mt-0.5 text-sm font-medium text-foreground break-words">{it.value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

const EASTERN_DIGITS = /[\u0660-\u0669\u06F0-\u06F9]/g;
/** One digit system on screen: Arabic-Indic digits in recorded names and quotations read as the Western digits used everywhere else. */
const westernDigits = (s: string) => s.replace(EASTERN_DIGITS, (d) => String(d.charCodeAt(0) - (d.charCodeAt(0) >= 0x06f0 ? 0x06f0 : 0x0660)));

/**
 * Words a party, a registry or a source wrote (case titles, company names, headlines). On an Arabic screen, demonstration tags never show,
 * abbreviations read as their Arabic terms, and an English passage is shown in its Arabic machine translation (marked, with the original on
 * hover) once the app's translator has it; English that remains is marked as English and isolated, so the line keeps its order.
 */
export function SourceText({ text: given, className }: { text?: string | number | null; className?: string }) {
  const lang = useContentLanguage();
  // A record may carry a number (a share, a count) where text was expected: it is shown, not allowed to take the screen down.
  const raw = given == null ? "" : String(given);
  if (!raw) return null;
  const ar = documentLocale() === "ar";
  const text = ar ? recordedTerms(arabicTerms(stripDemoTags(westernDigits(raw)))) : stripDemoTags(westernDigits(raw));
  if (!/[A-Za-z]/.test(text)) return <span className={className}>{text}</span>;
  const run = (p: string, key: number) => {
    if (ar && lang && isPassage(p)) {
      const hit = lang.get(p);
      if (hit) return <span key={key} lang="ar" title={p} className="decoration-sand-400 decoration-dotted underline-offset-4 hover:underline">{hit}</span>;
      lang.request(p);
    }
    return <bdi key={key} lang="en">{p}</bdi>;
  };
  if (!/[\u0600-\u06FF]/.test(text)) return <span className={className}>{run(text, 0)}</span>;
  const parts = text.split(LATIN_RUN);
  return <span className={className}>{parts.map((p, i) => (i % 2 ? run(p, i) : p))}</span>;
}
